import * as THREE from 'three';
import { BallTrajectoryData } from 'types/models/BallTrajectory';

/** Evaluate a polynomial (low-degree-first coefficients) at time t. */
export function evalPoly(coeffs: number[], t: number): number {
  let v = 0;
  let tp = 1;
  for (let i = 0; i < coeffs.length; i++) {
    v += coeffs[i] * tp;
    tp *= t;
  }
  return v;
}

/**
 * Convert a soccer-space point to a Three.js scene vector.
 *
 * Soccer X (depth from goal) → Three.js −Z (camera looks toward goal along −Z).
 * Soccer Y (height)          → Three.js Y.
 * Soccer Z (lateral)         → Three.js X.
 */
export function toScene(x: number, y: number, z: number): THREE.Vector3 {
  return new THREE.Vector3(z, y, -x);
}

/** Sample every segment to produce a chronologically ordered list of {t, pos} samples. */
export function sampleTrajectory(traj: BallTrajectoryData, samplesPerSecond = 120): { t: number; pos: THREE.Vector3 }[] {
  const out: { t: number; pos: THREE.Vector3 }[] = [];
  let elapsed = 0;
  for (const seg of traj.segments) {
    const [t0, t1] = seg.timeInterval;
    const duration = t1 - t0;
    const steps = Math.max(2, Math.ceil(duration * samplesPerSecond));
    for (let i = 0; i <= steps; i++) {
      const localT = t0 + (duration * i) / steps;
      const x = evalPoly(seg.x, localT);
      const y = evalPoly(seg.y, localT);
      const z = evalPoly(seg.z, localT);
      const pos = toScene(x, y, z);
      // Adjacent polynomial segments share an endpoint, so the last point of one segment and the
      // first of the next are (near) identical. Duplicate consecutive points make CatmullRomCurve3
      // "neck"/pinch the tube, which shows up as stutters along the flight path — drop them.
      const prev = out[out.length - 1];
      if (prev && prev.pos.distanceToSquared(pos) < 1e-6) continue;
      out.push({ t: elapsed + (localT - t0), pos });
    }
    elapsed += duration;
  }
  return out;
}

/** Get the ball position at absolute flight time t (seconds from first segment start). Clamped to trajectory bounds. */
export function positionAtTime(traj: BallTrajectoryData, t: number): THREE.Vector3 {
  let elapsed = 0;
  for (const seg of traj.segments) {
    const [t0, t1] = seg.timeInterval;
    const dur = t1 - t0;
    if (t <= elapsed + dur) {
      const local = t0 + (t - elapsed);
      return toScene(evalPoly(seg.x, local), evalPoly(seg.y, local), evalPoly(seg.z, local));
    }
    elapsed += dur;
  }
  const last = traj.segments[traj.segments.length - 1];
  return toScene(
    evalPoly(last.x, last.timeInterval[1]),
    evalPoly(last.y, last.timeInterval[1]),
    evalPoly(last.z, last.timeInterval[1]),
  );
}

/** Total flight duration across all segments (seconds). */
export function totalDuration(traj: BallTrajectoryData): number {
  return traj.segments.reduce((s, seg) => s + (seg.timeInterval[1] - seg.timeInterval[0]), 0);
}
